export const UI_LANG_STORAGE_KEY = 'notreu.uiLang';
export type UiLanguage = 'ja' | 'en';

/** 言語スイッチャーで明示的に選んだ回数。読み込み中の DB 値が選択を上書きしないようにする。 */
let explicitChoiceRevision = 0;

export function noteExplicitUiLanguageChoice(): void {
  explicitChoiceRevision += 1;
}

export function uiLanguageRevision(): number {
  return explicitChoiceRevision;
}

export function isUiLanguage(value: unknown): value is UiLanguage {
  return value === 'ja' || value === 'en';
}

/** Map an i18n language tag to the UI catalog. `en-US` is English, not Japanese. */
export function uiLanguageFromI18n(tag: string | undefined | null): UiLanguage {
  const primary = (tag || 'en').trim().toLowerCase().split(/[-_]/)[0];
  return primary === 'ja' ? 'ja' : 'en';
}

/** ブラウザの言語リストから、最初に対応している言語を返す。無ければ英語。 */
export function languageFromBrowser(languages?: readonly string[]): UiLanguage {
  const list =
    languages ??
    (typeof navigator !== 'undefined'
      ? navigator.languages?.length
        ? navigator.languages
        : [navigator.language]
      : []);

  for (const tag of list) {
    if (typeof tag !== 'string') continue;
    const primary = tag.trim().toLowerCase().split(/[-_]/)[0];
    if (primary === 'ja' || primary === 'en') return primary;
  }
  return 'en';
}

/**
 * DB の uiLanguage が ja / en ならそれを使う。
 * null・未設定・未知の値はブラウザ言語。対応言語が無ければ英語。
 */
export function resolveUiLanguage(stored: unknown, browserLanguages?: readonly string[]): UiLanguage {
  if (isUiLanguage(stored)) return stored;
  return languageFromBrowser(browserLanguages);
}

export function readCachedUiLanguage(): UiLanguage | null {
  try {
    const saved = globalThis.localStorage?.getItem(UI_LANG_STORAGE_KEY);
    return isUiLanguage(saved) ? saved : null;
  } catch {
    return null;
  }
}

export function cacheUiLanguage(next: UiLanguage): void {
  try {
    globalThis.localStorage?.setItem(UI_LANG_STORAGE_KEY, next);
  } catch {
    /* private mode */
  }
}

const LOGGED_OUT_LANGUAGE_PATHS = ['/sys/login', '/sys/ncg-apply'];

/** ログイン画面とアカウント作成画面。未ログインなので保存済み言語は使わない。 */
export function isLoggedOutLanguagePath(pathname?: string): boolean {
  const path = pathname ?? (typeof window !== 'undefined' ? window.location.pathname : '');
  return LOGGED_OUT_LANGUAGE_PATHS.some((item) => path === item || path.startsWith(`${item}/`));
}

/**
 * ログイン後の画面は、同じブラウザのキャッシュがあればそれ、無ければブラウザ言語。
 * ログイン前の画面は常にブラウザ言語。対応が無ければ英語。
 */
export function initialUiLanguage(pathname?: string): UiLanguage {
  if (isLoggedOutLanguagePath(pathname)) return languageFromBrowser();
  return readCachedUiLanguage() ?? languageFromBrowser();
}
